import type { ReactNode } from 'react'
import { useState } from 'react'
import { SectionCard } from '../../shared/section-card'
import { useCoachingStore } from '../../store'
import type { ReviewComment } from '../contracts'
import { CritiqueFlag } from '../critique-flag'

/**
 * Functional/Non-functional requirements list (spec §8.5, URL Shortener
 * seed) — a system-design-specific UI payload, not modeled on
 * `ReviewSubmission` (see A1's note on code-section.tsx). Rendered twice by
 * `system-design-review.tsx` — once for FR, once for NFR — with distinct
 * `sectionKey`/`prefix`/copy per instance, mirroring the `.dc` reference's
 * `mkReqList` helper.
 *
 * Each numbered row (`F{n}`/`N{n}`) is clickable, targeting
 * `{targetLabel} · {prefix}{n}`. One inline "✦ SUGGESTED" dashed row per
 * list (seeded FR: rate limiting, NFR: durability) accepts into the
 * next-numbered solid row with a trailing "✦ added" marker, or dismisses.
 * The slice has no add/dismiss-suggestion action (and isn't touched here) —
 * this is mock session state, kept local to this component per the ticket.
 */

export interface RequirementSuggestionSeed {
  id: string
  text: string
}

export function RequirementsList(props: {
  sectionKey: string
  title: string
  targetLabel: string
  prefix: string
  items: string[]
  seedSuggestion: RequirementSuggestionSeed
  comments?: ReviewComment[]
  anchorRef?: React.Ref<HTMLDivElement>
}): ReactNode {
  const {
    sectionKey,
    title,
    targetLabel,
    prefix,
    items,
    seedSuggestion,
    comments = [],
    anchorRef,
  } = props
  const { openComposer } = useCoachingStore().review
  const sectionComments = comments.filter((c) => c.section === sectionKey)

  const [suggestion, setSuggestion] = useState<{
    id: string
    text: string
    status: 'pending' | 'accepted' | 'dismissed'
  }>({ ...seedSuggestion, status: 'pending' })

  const rows = [
    ...items.map((text) => ({ text, added: false })),
    ...(suggestion.status === 'accepted'
      ? [{ text: suggestion.text, added: true }]
      : []),
  ]

  return (
    <SectionCard
      title={title}
      commentCount={sectionComments.length}
      onAddComment={() =>
        openComposer(sectionKey, `${targetLabel} · Overall`, 'critique')
      }
      anchorRef={anchorRef}
    >
      {sectionComments.map((c) => (
        <CritiqueFlag key={c.id} comment={c} />
      ))}

      <div className="mb-2.5 text-[11.5px] text-(--cd-text-faint)">
        Click any requirement to comment on it specifically.
      </div>

      <div className="flex flex-col gap-1.5">
        {rows.map((row, idx) => {
          const n = idx + 1
          return (
            <button
              key={`${sectionKey}-${n}`}
              type="button"
              onClick={() =>
                openComposer(
                  sectionKey,
                  `${targetLabel} · ${prefix}${n}`,
                  'critique',
                )
              }
              className="flex w-full items-baseline gap-2.5 rounded-[9px] border border-(--cd-border-2) bg-(--cd-inset) px-3.5 py-2 text-left"
            >
              <span className="shrink-0 font-mono text-[11px] text-(--cd-text-faint)">
                {prefix}
                {n}
              </span>
              <span className="flex-1 text-[13px] leading-relaxed text-(--cd-text-2)">
                {row.text}
              </span>
              {row.added && (
                <span className="shrink-0 text-[10px] font-bold text-(--cd-purple)">
                  ✦ added
                </span>
              )}
            </button>
          )
        })}

        {suggestion.status === 'pending' && (
          <div
            className="flex items-center gap-2.5 rounded-[9px] border border-dashed px-3.5 py-2"
            style={{
              background:
                'color-mix(in oklab, var(--cd-purple) 5%, transparent)',
              borderColor:
                'color-mix(in oklab, var(--cd-purple) 35%, transparent)',
            }}
          >
            <span className="shrink-0 text-[10px] font-bold text-(--cd-purple)">
              ✦ SUGGESTED
            </span>
            <span className="flex-1 text-[13px] leading-relaxed text-(--cd-text-2)">
              {suggestion.text}
            </span>
            <button
              type="button"
              onClick={() =>
                setSuggestion((s) => ({ ...s, status: 'accepted' }))
              }
              className="shrink-0 rounded-full border px-2.5 py-1 text-[11px] font-semibold text-(--cd-green)"
              style={{
                background:
                  'color-mix(in oklab, var(--cd-green) 8%, transparent)',
                borderColor:
                  'color-mix(in oklab, var(--cd-green) 30%, transparent)',
              }}
            >
              + Add
            </button>
            <button
              type="button"
              onClick={() =>
                setSuggestion((s) => ({ ...s, status: 'dismissed' }))
              }
              className="shrink-0 text-[11px] text-(--cd-text-faint)"
            >
              ✕
            </button>
          </div>
        )}
      </div>
    </SectionCard>
  )
}
